/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose, withHandlers, withProps, withStateHandlers } from 'recompose';
import {
  debounce,
  flatten,
  flow,
  get,
  map,
  omitBy,
  isEqual,
  isEmpty,
  isNil,
  isNumber,
  orderBy,
  pick,
  values,
  without
} from 'lodash/fp';

import LeaderBoardFilterBar from 'app/components/challenge/LeaderBoardFilterBar';
import LeaderBoardHead from 'app/components/challenge/LeaderBoardHead';
import LeaderBoardTable from 'app/components/challenge/LeaderBoardTable';
import MyLeaderRow from 'app/components/challenge/MyLeaderRow';
import PaginationBox from 'app/components/commons/PaginationBox';
import Responsive from 'app/components/responsive/Responsive';
import SimpleSticky from 'app/components/commons/SimpleSticky';
import { PRIZE, STATUS } from 'app/constants/leaderBoardColumnTypes';
import { COMPLETED } from 'app/constants/challengeStatusTypes';
import { fetchOpponentEntry, fetchPrizes } from 'app/actions/challengeActions';
import decodeCompetitor from 'app/normalizers/decodeCompetitor';
import { getChallengeEntries, getChallengeProjectedEntries, getPrizes } from 'app/api/challenges';
import withAPICollection from 'app/hoc/withAPICollection';
import ProjectedRacingScore from './ProjectedRacingScore';
import RacingScoresDescription from './RacingScoresDescription';

const omitNil = omitBy(isNil);
const pickFilterFields = pick([
  'filteredCountryId',
  'filteredGroupId',
  'myEntriesOnly',
  'selectedPlayers',
  'selectedTeams'
]);

const getPointsToCash = (prizes: Array<any>) => {
  if (prizes.length === 0) return;

  const bottomPoints = prizes
    .filter(prize => isNumber(prize.min_points))
    .map(prize => prize.min_points)
    .pop();

  if (bottomPoints === null) return 0;

  return bottomPoints;
};

type Props = {
  competitors: Array<Object>,
  challengeStatusId: number,
  currentPage: number,
  displayCountry?: boolean,
  displayPlayersCount: boolean,
  fetchPrizes: (id: string) => void,
  hasProjections: boolean,
  isEntryLoading: boolean,
  isLoading: boolean,
  isRacing?: boolean,
  isSticky?: boolean,
  isTicketChallenge: boolean,
  isTrainingChallenge: boolean,
  lastPage?: number,
  offsetTop: number,
  onEntrySelect: string => void,
  onFetch: (?Object) => void,
  onProjectedChange: () => void,
  onSilentFetch: (?Object) => void,
  opponentEntryId?: string,
  players: Array<Object>,
  pollTimestamp: number,
  prizes: Array<any>,
  showProjected: boolean,
  showProjectionForPlayers: Array<any>,
  userEntryId?: string,
  userHasEntered: boolean
};

type State = {
  dynamicColumn: PRIZE | STATUS,
  filteredCountryId: ?number,
  filteredGroupId: ?number | ?string,
  isFilterOpen: boolean,
  myEntriesOnly: boolean,
  selectedPlayers: Array<string>,
  selectedTeams: Array<string>
};

class LeaderBoard extends React.Component<Props, State> {
  props: Props;

  state: State = {
    dynamicColumn: PRIZE,
    filteredCountryId: undefined,
    filteredGroupId: undefined,
    isFilterOpen: false,
    myEntriesOnly: false,
    searchTerm: undefined,
    showProjected: false,
    selectedPlayers: [],
    selectedTeams: []
  };

  static defaultProps = {
    displayCountry: false,
    isRacing: false,
    isSticky: false,
    lastPage: undefined,
    opponentEntryId: undefined,
    userEntryId: undefined
  };

  componentDidMount() {
    const { onFetch, match, fetchPrizes } = this.props;

    onFetch(this.getSelectedEntityIds());
    fetchPrizes(match.params.id);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const {
      currentPage,
      onFetch,
      onSilentFetch,
      pollTimestamp,
      showProjected,
      fetchPrizes,
      match
    } = this.props;

    const { filteredCountryId, filteredGroupId, myEntriesOnly, searchTerm } = this.state;

    if (
      showProjected !== prevProps.showProjected ||
      !isEqual(pickFilterFields(prevState), pickFilterFields(this.state))
    ) {
      onFetch({
        countries: filteredCountryId,
        groups: filteredGroupId,
        page: 1,
        user_only: myEntriesOnly,
        ...this.getSelectedEntityIds()
      });
      fetchPrizes(match.params.id);
    } else if (pollTimestamp !== prevProps.pollTimestamp) {
      onSilentFetch({
        countries: filteredCountryId,
        groups: filteredGroupId,
        page: currentPage,
        user_only: myEntriesOnly,
        user_name: searchTerm,
        ...this.getSelectedEntityIds()
      });
      fetchPrizes(match.params.id);
    }
  }

  getSelectedEntityIds = () => {
    const { selectedPlayers, selectedTeams } = this.state;
    const ids = {};

    if (selectedPlayers.length > 0) {
      ids.player_ids = selectedPlayers.join(',');
    }
    if (selectedTeams.length > 0) {
      ids.team_ids = selectedTeams.join(',');
    }

    return ids;
  };

  handleColumnChange = (column: PRIZE | STATUS) => {
    this.setState({
      dynamicColumn: column
    });
  };

  handleEntrySelect = (entryId: string) => {
    const { isEntryLoading, onEntrySelect, opponentEntryId } = this.props;

    if (!isEntryLoading && opponentEntryId !== entryId) {
      onEntrySelect(entryId);
    }
  };

  handleCountryChange = (countryId: number) => {
    this.setState(state => ({
      filteredCountryId: countryId !== state.filteredCountryId ? countryId : undefined,
      filteredGroupId: undefined,
      myEntriesOnly: false,
      searchTerm: undefined
    }));
  };

  handleEntriesChange = () => {
    this.setState(state => ({
      filteredCountryId: undefined,
      filteredGroupId: undefined,
      searchTerm: undefined,
      myEntriesOnly: !state.myEntriesOnly
    }));
  };

  handleGroupChange = (groupId: number) => {
    this.setState(state => ({
      filteredCountryId: undefined,
      filteredGroupId: groupId !== state.filteredGroupId ? groupId : undefined,
      myEntriesOnly: false,
      searchTerm: undefined
    }));
  };

  handlePageChange = (page: number) => {
    const { onFetch } = this.props;
    const { filteredCountryId, filteredGroupId, myEntriesOnly, searchTerm } = this.state;

    onFetch({
      countries: filteredCountryId,
      groups: filteredGroupId,
      page,
      user_only: myEntriesOnly,
      user_name: searchTerm,
      ...this.getSelectedEntityIds()
    });
  };

  handleSearchChange = (value: string) => {
    const { onFetch } = this.props;

    this.setState(
      () => ({
        filteredCountryId: undefined,
        filteredGroupId: undefined,
        myEntriesOnly: false,
        searchTerm: value
      }),
      debounce(1000, () => {
        const { filteredCountryId, filteredGroupId, myEntriesOnly, searchTerm } = this.state;

        onFetch({
          countries: filteredCountryId,
          groups: filteredGroupId,
          user_only: myEntriesOnly,
          user_name: searchTerm,
          ...this.getSelectedEntityIds()
        });
      })
    );
  };

  handleSearchClear = () => {
    const { onFetch } = this.props;

    this.setState(() => ({
      searchTerm: undefined,
      filteredCountryId: undefined,
      filteredGroupId: undefined
    }));

    onFetch(this.getSelectedEntityIds());
  };

  handleReset = () => {
    this.setState({
      filteredCountryId: undefined,
      filteredGroupId: undefined,
      myEntriesOnly: false,
      selectedPlayers: []
    });
  };

  getFilterHash() {
    const { currentPage, opponentEntryId, showProjected } = this.props;
    const { dynamicColumn, filteredCountryId, filteredGroupId, myEntriesOnly } = this.state;

    return JSON.stringify(
      omitNil({
        currentPage,
        dynamicColumn,
        filteredCountryId,
        filteredGroupId,
        myEntriesOnly,
        opponentEntryId,
        showProjected
      })
    );
  }

  toggleFilter = () => {
    this.setState(state => ({
      isFilterOpen: !state.isFilterOpen
    }));
  };

  handleSelectEntity = entity => {
    const { selectedPlayers, selectedTeams } = this.state;

    if (entity.isTeam) {
      this.setState({
        selectedTeams: selectedTeams.includes(entity.id)
          ? without([entity.id], selectedTeams)
          : [...selectedTeams, entity.id]
      });
    } else {
      this.setState({
        selectedPlayers: selectedPlayers.includes(entity.id)
          ? without([entity.id], selectedPlayers)
          : [...selectedPlayers, entity.id]
      });
    }
  };

  render() {
    const {
      challengeStatusId,
      competitors,
      currentPage,
      displayCountry,
      displayPlayersCount,
      hasProjections,
      isLoading,
      isRacing,
      isSticky,
      isTicketChallenge,
      isTrainingChallenge,
      lastPage,
      offsetTop,
      onProjectedChange,
      opponentEntryId,
      showProjectionForPlayers,
      players,
      prizes,
      showProjected,
      userEntryId,
      userHasEntered
    } = this.props;

    const {
      dynamicColumn,
      filteredCountryId,
      filteredGroupId,
      isFilterOpen,
      myEntriesOnly,
      searchTerm,
      selectedPlayers,
      selectedTeams
    } = this.state;

    return (
      <div>
        <SimpleSticky isStuckFunc={isStuck => isStuck && isSticky} offsetTop={offsetTop}>
          {isRacing && showProjectionForPlayers.length > 0 && (
            <ProjectedRacingScore players={showProjectionForPlayers} />
          )}
          {isRacing && <RacingScoresDescription />}
          <div>
            <Responsive mobile>
              {isMobile => (
                <div>
                  {!isTrainingChallenge && (
                    <LeaderBoardFilterBar
                      filteredCountryId={filteredCountryId}
                      filteredGroupId={filteredGroupId}
                      isOpen={isFilterOpen}
                      myEntriesOnly={myEntriesOnly}
                      players={orderBy(
                        ['selected', 'score', 'popularity'],
                        ['desc', 'desc', 'desc']
                      )(players)}
                      onCountryChange={this.handleCountryChange}
                      onSearchChange={this.handleSearchChange}
                      onSearchClear={this.handleSearchClear}
                      onEntriesChange={this.handleEntriesChange}
                      onFilterClick={this.toggleFilter}
                      onGroupChange={this.handleGroupChange}
                      onSelectEntity={this.handleSelectEntity}
                      onReset={this.handleReset}
                      searchTerm={searchTerm}
                      selectedPlayers={selectedPlayers}
                      selectedTeams={selectedTeams}
                    />
                  )}
                  <LeaderBoardHead
                    displayPlayersCount={displayPlayersCount}
                    isRunning={challengeStatusId < COMPLETED}
                    dynamicColumn={dynamicColumn}
                    hasProjections={hasProjections}
                    onColumnSelect={this.handleColumnChange}
                    onProjectedChange={onProjectedChange}
                    ptsToCash={getPointsToCash(prizes)}
                    showProjected={showProjected}
                  />
                  {isMobile && userHasEntered && (
                    <MyLeaderRow
                      displayPlayersCount={displayPlayersCount}
                      dynamicColumn={dynamicColumn}
                      onDynamicColumnSelect={this.handleColumnChange}
                      onEntrySelect={this.handleEntrySelect}
                      showProjected={showProjected}
                    />
                  )}
                </div>
              )}
            </Responsive>
          </div>
        </SimpleSticky>
        <LeaderBoardTable
          competitors={competitors}
          displayCountry={displayCountry}
          displayPlayersCount={displayPlayersCount}
          dynamicColumn={dynamicColumn}
          filterHash={this.getFilterHash()}
          isLoading={isLoading}
          isTicketChallenge={isTicketChallenge}
          onEntrySelect={this.handleEntrySelect}
          opponentEntryId={opponentEntryId}
          showProjected={showProjected}
          userEntryId={userEntryId}
        />
        <PaginationBox
          currentPage={currentPage}
          lastPage={lastPage}
          onChange={this.handlePageChange}
        />
      </div>
    );
  }
}

const apiWrapper = compose(
  withStateHandlers(
    {
      showProjected: false
    },
    {
      onProjectedChange: ({ showProjected }) => () => ({
        showProjected: !showProjected
      })
    }
  ),
  withProps(({ hasProjections, showProjected }) => ({
    showProjected: hasProjections && showProjected
  })),
  withAPICollection(({ showProjected }) => ({
    fetcher: showProjected ? getChallengeProjectedEntries : getChallengeEntries,
    normalizeData: map(decodeCompetitor)
  })),
  withProps(({ items, loading }) => ({
    competitors: items,
    isLoading: loading
  })),
  withHandlers({
    onFetch: ({ match, onFetch }) => query => {
      onFetch(match.params.id, query);
    },
    onSilentFetch: ({ onSilentFetch, match }) => query => {
      onSilentFetch(match.params.id, query);
    }
  })
);

const connector = connect(
  state => ({
    challengeStatusId: get('challenge.statusId', state),
    displayCountry: state.settings.displayCountry,
    isEntryLoading: state.entry.isLoadingOpponent,
    isTrainingChallenge: state.challenge.isTrainingChallenge,
    isTicketChallenge: state.challenge.isTicketChallenge,
    opponentEntryId: state.entry.opponentEntry.id,
    showProjectionForPlayers: !isEmpty(get('entry.myEntry.playersSubmitted', state))
      ? flow(values, flatten)(get('entry.myEntry.playersSubmitted', state))
      : [],
    prizes: state.challenge.prizes,
    userEntryId: state.entry.myEntry.id,
    userHasEntered: state.challenge.hasEntered
  }),
  {
    onEntrySelect: fetchOpponentEntry,
    fetchPrizes: fetchPrizes
  }
);

export default compose(withRouter, apiWrapper, connector)(LeaderBoard);
